import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PhoneOff, Clock, PhoneIncoming } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const workspaceFromPath = (p) => (p.match(/^\/workspace\/([^/]+)/) || [])[1];

export const ConcurrencyWatcher = () => {
  const { api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const ws = workspaceFromPath(location.pathname);
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const shownAtLimit = useRef(false);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (!ws) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/api/workspaces/${ws}/concurrency`);
        if (!alive) return;
        setState(data);
        const key = `cc_limit_seen_${ws}`;
        if (!data.at_limit) { shownAtLimit.current = false; sessionStorage.removeItem(key); }
        const newLimitEpisode = data.at_limit && !shownAtLimit.current && !sessionStorage.getItem(key);
        if (data.rejected_since_ack > 0 || newLimitEpisode) {
          shownAtLimit.current = true; sessionStorage.setItem(key, '1'); setOpen(true);
        }
      } catch { /* passive */ }
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => { alive = false; clearInterval(id); };
  }, [api, ws]);

  const close = () => {
    setOpen(false);
    if (ws) api.post(`/api/workspaces/${ws}/concurrency/ack`).catch(() => {});
  };
  const goBilling = () => { close(); navigate(`/workspace/${ws}/billing?manage=1#call-lines`); };

  if (!state) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <DialogContent className={`max-w-md ${isDarkMode ? "bg-[#0d0f14] border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"}`} data-testid="concurrency-limit-popup">
        <DialogTitle className="text-white flex items-center gap-2"><PhoneOff size={18} className="text-rose-300" />You're at your call limit</DialogTitle>
        <DialogDescription className="text-gray-400">
          {state.live.total} of {state.limit} call lines are in use across your sub-accounts.
          If you'd like more concurrent calls, add concurrent call lines.
        </DialogDescription>
        <div className="space-y-3 text-sm text-gray-300 mt-2">
          <p className="flex gap-2"><Clock size={15} className="mt-0.5 text-amber-300 shrink-0" />New outbound calls wait in a queue and dial automatically, with a delay, as soon as a line frees up.{state.queued > 0 ? ` ${state.queued} waiting now.` : ''}</p>
          <p className="flex gap-2"><PhoneIncoming size={15} className="mt-0.5 text-teal-300 shrink-0" />One extra inbound call can still come through. Beyond that, callers hear a busy message, the call shows in your inbox as "Concurrent limit reached" and your post-call webhook still runs.</p>
          {state.rejected_since_ack > 0 && (
            <p className="rounded-lg bg-rose-400/10 border border-rose-400/30 px-3 py-2 text-rose-200" data-testid="concurrency-rejected-count">
              {state.rejected_since_ack} inbound call{state.rejected_since_ack === 1 ? ' was' : 's were'} not answered because every line was busy.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={close} className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-white/5" data-testid="concurrency-popup-dismiss">Not now</button>
          <button type="button" onClick={goBilling} className="px-4 py-2 rounded-lg text-sm font-semibold bg-teal-400 text-[#06201d] hover:bg-teal-300" data-testid="concurrency-popup-add-lines">Add call lines</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConcurrencyWatcher;
